import * as THREE from 'three';
import { ThreeAxisColors as axisColors, ThreeAxisMaterials as axisMaterials } from '../constants/axisConfig';

export function scaleTranslateArrowheads(tc, { radius = 1.5, length = 1.0 } = {}) {
  const translate = tc?._gizmo?.gizmo?.translate;
  if (!translate) return;

  // Helper: axis index map
  const AX = { X: 0, Y: 1, Z: 2 };

  translate.children.forEach(mesh => {
    if (!mesh.isMesh) return;
    const axisName = mesh.name;             // 'X' | 'Y' | 'Z' | 'XY'...
    if (!AX.hasOwnProperty(axisName)) return;

    // Compute bounding box so we can distinguish arrowheads from shafts
    const g = mesh.geometry;
    g.computeBoundingBox();
    const bb = g.boundingBox; // in local space (already baked orientation/position)
    const size = new THREE.Vector3().subVectors(bb.max, bb.min);

    // Shafts are ~0.5 long; arrowheads are ~0.1 long in stock file
    const axis = AX[axisName];
    const axisLen = size.getComponent(axis);

    // Heuristic: treat anything shorter than ~0.2 as an arrowhead
    if (axisLen >= 0.2) return;

    // Figure out which end is the *base* (closer to origin), so we can keep it anchored
    const minA = bb.min.getComponent(axis);
    const maxA = bb.max.getComponent(axis);
    // Base is the boundary closer to 0; tip is farther from 0
    const baseCoord = (Math.abs(minA) < Math.abs(maxA)) ? minA : maxA;

    // Build a scale that:
    // - scales along the axis by `length`
    // - scales perpendicular by `radius`
    const sx = (axis === 0) ? length : radius;
    const sy = (axis === 1) ? length : radius;
    const sz = (axis === 2) ? length : radius;

    // Translate so base lies at origin -> scale -> translate back
    const T1 = new THREE.Matrix4();
    const S  = new THREE.Matrix4();
    const T2 = new THREE.Matrix4();

    const t = new THREE.Vector3(0, 0, 0);
    t.setComponent(axis, -baseCoord);
    T1.makeTranslation(t.x, t.y, t.z);

    S.makeScale(sx, sy, sz);

    t.setComponent(axis, baseCoord);
    T2.makeTranslation(t.x, t.y, t.z);

    const M = new THREE.Matrix4().multiplyMatrices(T2, S).multiply(T1);

    // If we already installed a custom geometry, dispose it before replacing
    if (mesh.userData._customArrowGeom) {
      mesh.geometry.dispose();
    }

    const newG = g.clone();
    newG.applyMatrix4(M);
    newG.computeBoundingSphere();
    newG.computeBoundingBox();

    mesh.geometry = newG;
    mesh.userData._customArrowGeom = true;
  });
}

const configureTransformControls = tc => {
  const gizmo = tc.getHelper();
  const gizmoRoot = tc._gizmo;
  if (!gizmo) return;

  // 1) color arrowheads & circle lines
    gizmo.traverse(child => {
        if (child.material && child.name) {
        const axis = child.name.charAt(0); // “X” or “Y” or “Z”
        if (axisColors[axis]) {
            // child.material.color = colors[axis];
            child.material = axisMaterials[axis];
        }
        child.material.depthTest = false;
        child.material.depthWrite = false;
        child.material.transparent = true;
        }
    });

    // 2) translate mode: hide *only* the shafts
    if (tc.mode === 'translate') 
    {
        const translateGroup = gizmoRoot.gizmo.translate;
        const objectsToDispose = [];
        // walk its Mesh children and zero out the long cylinders
        translateGroup.traverse(child => {
            if (child.isMesh &&
            child.geometry instanceof THREE.CylinderGeometry &&
            child.geometry.parameters.radialSegments === 3
            ) {
                objectsToDispose.push(child);
            }
        });
        objectsToDispose.forEach(obj => {
            obj.removeFromParent();

            // optional: clean up memory
            obj.geometry.dispose();
            obj.material.dispose();
        });
    }

  // 3) rotate mode: replace each half-arc with a full circle
  if (tc.mode === 'rotate') {
    const handlesToRemove = [];
    gizmoRoot.gizmo.rotate.traverse(child => {
        if (child.name.includes('X') ||
            child.name.includes('Y') ||
            child.name.includes('Z')) {
            handlesToRemove.push(child);
        }
    });
    handlesToRemove.forEach(child => {
        child.removeFromParent();
        child.geometry.dispose();
    });

    const pickerHandlesToRemove = [];
    gizmoRoot.picker.rotate.traverse(child => {
        if (child.name.includes('X') ||
            child.name.includes('Y') ||
            child.name.includes('Z')) {
            pickerHandlesToRemove.push(child);
        }
    });
    pickerHandlesToRemove.forEach(child => {
        child.removeFromParent();
        child.geometry.dispose();
    });

    const eGizmo = gizmoRoot.gizmo.rotate.getObjectByName('E');
    if (eGizmo) {
        // Clone the material so our color changes don't affect other things
        eGizmo.material = eGizmo.material.clone();
        eGizmo.material.color.set( 0x000000);
        eGizmo.material.depthTest = false;
        eGizmo.material.depthWrite = false;
        eGizmo.material.transparent = true;
    }
  }

  // 4) remove the little XY/YZ/XZ pick‐planes
  ['XY', 'YZ', 'XZ', 'XYZ'].forEach(name => {
    const plane = gizmo.getObjectByName(name);
    if (plane) plane.removeFromParent();

    const plane2 = gizmoRoot.picker.translate.getObjectByName(name);
    if(plane2) plane2.removeFromParent();
  });
};

export default configureTransformControls;